import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contact`);
        if (response.ok) {
          const data = await response.json();
          setContactInfo(data);
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      }
    };
    fetchContactInfo();
  }, [API_URL]);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    constituency: '',
    interest: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Thank you for your interest! We will contact you shortly.');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      constituency: '',
      interest: '',
      message: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Use fetched data or fallback to defaults
  const email = contactInfo?.email || 'contact@amical.co.in';
  const phone = contactInfo?.phone || '+91-XXXXXXXXXX';
  const address = contactInfo?.address || 'AEPL\nMaharashtra, India';
  const benefits = contactInfo?.benefits || [
    'Proven track record with 350+ villages served',
    'Data-driven solutions for measurable impact',
    'Comprehensive training and ongoing support',
    'Technology that truly serves the people'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-semibold mb-6">
            Begin Your Constituency's Transformation
          </h1>
          <p className="text-xl opacity-95">
            Let's discuss how Gramtek solutions can help you serve your constituents better.
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-semibold mb-8 text-[#1E3A8A]">
                Schedule a Free Consultation
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="fullName" className="text-[#374151] text-base mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className="h-12 border-2 border-[#E5E7EB] focus:border-[#1E3A8A]"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#374151] text-base mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="h-12 border-2 border-[#E5E7EB] focus:border-[#1E3A8A]"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#374151] text-base mb-2 block">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="h-12 border-2 border-[#E5E7EB] focus:border-[#1E3A8A]"
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>

                <div>
                  <Label htmlFor="constituency" className="text-[#374151] text-base mb-2 block">
                    Constituency / Position *
                  </Label>
                  <Input
                    id="constituency"
                    type="text"
                    required
                    value={formData.constituency}
                    onChange={(e) => handleChange('constituency', e.target.value)}
                    className="h-12 border-2 border-[#E5E7EB] focus:border-[#1E3A8A]"
                    placeholder="e.g., MLA - Pune North"
                  />
                </div>

                <div>
                  <Label htmlFor="interest" className="text-[#374151] text-base mb-2 block">
                    Specific Interest *
                  </Label>
                  <Select value={formData.interest} onValueChange={(value) => handleChange('interest', value)} required>
                    <SelectTrigger className="h-12 border-2 border-[#E5E7EB] focus:border-[#1E3A8A]">
                      <SelectValue placeholder="Select your area of interest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pds">Gramtek PDS</SelectItem>
                      <SelectItem value="crs">Complaint Resolution System</SelectItem>
                      <SelectItem value="kachurwahi">Kachurwahi Mission</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp Integration</SelectItem>
                      <SelectItem value="general">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#374151] text-base mb-2 block">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="min-h-[120px] border-2 border-[#E5E7EB] focus:border-[#1E3A8A]"
                    placeholder="Tell us more about your needs..."
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full h-12 bg-[#EA580C] hover:bg-[#DC4D09] text-white text-lg"
                >
                  Submit
                </Button>
              </form>
            </div>

            {/* Company Information */}
            <div>
              <h2 className="text-3xl font-semibold mb-8 text-[#1E3A8A]">
                Get in Touch
              </h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-[#1E3A8A]">Email</h3>
                    <a href={`mailto:${email}`} className="text-lg text-[#374151] hover:text-[#EA580C] transition-colors">
                      {email}
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-[#1E3A8A]">Phone</h3>
                    <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="text-lg text-[#374151] hover:text-[#EA580C] transition-colors">
                      {phone}
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-[#1E3A8A]">Address</h3>
                    <p className="text-lg text-[#374151] leading-relaxed whitespace-pre-line">
                      {address}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-[#F9FAFB] rounded-lg border-2 border-[#E5E7EB]">
                <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">
                  Why Partner With Us?
                </h3>
                <ul className="space-y-3 text-[#374151]">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-[#EA580C] font-bold mt-1">•</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;